import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import serialize from 'form-serialize';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Rating from '@prisjakt/ui-rating';
import Button from '@prisjakt/ui-button';
import { TextInput, TextArea } from '@prisjakt/ui-inputs';
import T from '@prisjakt/ui-translate';
import actions from '../../actions';
import Modal from '../../../../components/modal';
import SubmitButton from '../../../account/components/submit-button';

import './index.scss';

const styles = bemGenerator('product-user-review-dialog', [
  'section',
  'controls',
  'title',
  'info',
  'rating',
  'input-title',
  'error-info',
  'text-area',
  'cancel-button',
]);

function isValidDate(dateString) {
  const regEx = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateString.match(regEx)) return false; // Invalid format
  const d = new Date(dateString);
  if (!d.getTime() && d.getTime() !== 0) return false; // Invalid date
  return d.toISOString().slice(0, 10) === dateString;
}

class UserReviewDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      reviewDraft: {
        rating: 0,
        text: '',
      },
    };

    this.validators = {
      rating: rating =>
        rating > 0 || this.context.t('reviews', 'dialog.rating-required-error'),
      reviewText: reviewText =>
        reviewText.trim().length > 0 ||
        this.context.t('reviews', 'dialog.text-required-error'),
      purchaseDate: purchaseDate => {
        if (!this.props.setPurchaseDate) {
          return true;
        }
        return (
          isValidDate(purchaseDate) ||
          this.context.t('reviews', 'dialog.date-invalid-error')
        );
      },
      displayName: displayName =>
        !this.props.setDisplayName
          ? true
          : displayName.trim().length > 0 ||
            this.context.t('reviews', 'dialog.name-required-error'),
    };

    this.submit = this.submit.bind(this);
    this.cancel = this.cancel.bind(this);
    this.clearErrors = this.clearErrors.bind(this);
  }

  validate(data) {
    const fields = Object.keys(this.validators);
    const errors = fields.reduce((err, key) => {
      const valueToValidate = data[key];
      const response = this.validators[key](valueToValidate);
      return response === true ? err : { ...err, [key]: response };
    }, {});

    return { isValid: Object.keys(errors).length === 0, errors };
  }

  submit(e, intent) {
    e.preventDefault();
    const {
      reviewText = '',
      rating = 0,
      displayName = '',
      purchaseDate = '',
    } = serialize(e.target, {
      hash: true,
    });

    if (intent !== 'delete') {
      const { isValid, errors } = this.validate({
        reviewText,
        rating,
        displayName,
        purchaseDate,
      });

      if (!isValid) {
        this.setState({ errors });
        return;
      }
    }

    switch (intent || e.target.dataset.intent) {
      case 'create':
        this.props.createReview({
          rating,
          reviewText,
          displayName,
          ...(this.props.setPurchaseDate && { purchaseDate }),
          belongsToId: this.props.belongsToId,
          reviewType: this.props.reviewType,
        });
        break;
      case 'edit':
        this.props.editReview({
          rating,
          reviewText,
          ...(this.props.setPurchaseDate && { purchaseDate }),
          reviewId: this.props.myReview.id,
          reviewType: this.props.reviewType,
          belongsToId: this.props.belongsToId,
        });
        break;
      case 'delete':
        this.props.deleteReview({
          reviewId: this.props.myReview.id,
          reviewType: this.props.reviewType,
          belongsToId: this.props.belongsToId,
        });
        break;
      default:
        break;
    }
  }

  cancel(e) {
    e.preventDefault();
    this.props.closeReviewDialog();
  }

  clearErrors(e) {
    const inputName = e.target.name;
    this.setState(prevState => {
      const { [inputName]: remove, ...errors } = prevState.errors;
      return { errors };
    });
  }

  saveReviewDraft() {
    if (this.form) {
      const { reviewText, rating } = serialize(this.form, {
        hash: true,
      });

      this.setState({
        reviewDraft: {
          text: reviewText,
          rating,
        },
      });
    }
  }

  renderDialog() {
    const { t, market } = this.context;
    const { pendingRequest, requestError, myReview, title } = this.props;

    const showSubmitSpinner =
      pendingRequest &&
      (pendingRequest.type === 'create' || pendingRequest.type === 'edit');

    const showDeleteSpinner =
      pendingRequest && pendingRequest.type === 'delete';

    return (
      <form
        ref={ref => {
          this.form = ref;
        }}
        data-intent={myReview ? 'edit' : 'create'}
        onSubmit={this.submit}
        method="POST"
        className={styles.block}
      >
        <h3 className={styles.title}>{title}</h3>

        {requestError && (
          <p
            className={styles['error-info'].withModifier({
              'regular-size': true,
            })}
          >
            {t('reviews', 'dialog.unknown-error')}
          </p>
        )}

        <div className={styles.section}>
          <p className={styles['input-title']}>
            {t('reviews', 'dialog.rate-info')}
          </p>

          <span
            className={styles.rating.withModifier({
              error: this.state.errors.rating,
            })}
          >
            <Rating
              rate={myReview ? myReview.rating : this.state.reviewDraft.rating}
              canRate
              onChange={this.clearErrors}
            />
          </span>

          {this.state.errors.rating && (
            <p className={styles['error-info']}>{this.state.errors.rating}</p>
          )}
        </div>

        <div className={styles.section}>
          <TextArea
            name="reviewText"
            label={t('reviews', 'dialog.text-area-label')}
            tabIndex="0"
            helper={
              this.state.errors.reviewText || (
                <T domain="reviews">dialog.rules.md</T>
              )
            }
            warning={Boolean(this.state.errors.reviewText)}
            onChange={this.clearErrors}
            defaultValue={
              myReview ? myReview.reviewText : this.state.reviewDraft.text
            }
          />

          {this.props.setPurchaseDate && (
            <div>
              <p className={styles['input-title']}>
                {t('reviews', 'dialog.purchase-date')}
              </p>
              <input
                type="date"
                name="purchaseDate"
                placeholder={
                  market.modules.userReviewPurchaseDate.datePlaceholderFormat
                }
                onChange={this.clearErrors}
                defaultValue={myReview && myReview.purchaseDate}
              />
              {this.state.errors.purchaseDate && (
                <p className={styles['error-info']}>
                  {this.state.errors.purchaseDate}
                </p>
              )}
            </div>
          )}
        </div>

        {this.props.setDisplayName && (
          <div className={styles.section}>
            <TextInput
              name="displayName"
              label={t('reviews', 'dialog.display-name-label')}
              onChange={this.clearErrors}
              tabIndex="0"
              helper={
                this.state.errors.displayName
                  ? this.state.errors.displayName
                  : t('reviews', 'dialog.display-name-info')
              }
              warning={Boolean(this.state.errors.displayName)}
              type="text"
            />
          </div>
        )}

        <div className={styles.controls}>
          {this.props.myReview && (
            <SubmitButton
              onClick={e => this.submit(e, 'delete')}
              color="negative"
              variant="outlined"
              loading={showDeleteSpinner}
              text={t('reviews', 'dialog.delete')}
            />
          )}
          <Button
            className={styles['cancel-button'].withModifier({
              'align-right': this.props.myReview,
            })}
            onClick={this.cancel}
            color="primary"
            variant="outlined"
          >
            {t('reviews', 'dialog.cancel')}
          </Button>
          <SubmitButton
            color="positive"
            loading={showSubmitSpinner}
            text={
              this.props.myReview
                ? t('reviews', 'dialog.update')
                : t('reviews', 'dialog.create')
            }
          />
        </div>
      </form>
    );
  }

  render() {
    return (
      <Modal
        width={500}
        onRequestClose={() => {
          this.saveReviewDraft();
          this.props.closeReviewDialog();
        }}
        isOpen={this.props.isOpen}
      >
        {this.renderDialog()}
      </Modal>
    );
  }
}

const { func, number, bool, string, shape, oneOf, oneOfType } = PropTypes;
UserReviewDialog.propTypes = {
  belongsToId: number.isRequired, // i.e. product id, store id
  title: string.isRequired,
  reviewType: oneOf(['PRODUCT', 'STORE']).isRequired,
  myReview: shape({
    id: string,
    rating: number,
    reviewText: string,
    createdAt: string,
    updatedAt: string,
    purchaseDate: string,
  }),

  isOpen: bool.isRequired,
  setDisplayName: bool.isRequired,
  pendingRequest: oneOfType([bool, shape({ type: string })]),
  requestError: bool,
  setPurchaseDate: bool,

  closeReviewDialog: func.isRequired,
  createReview: func.isRequired,
  editReview: func.isRequired,
  deleteReview: func.isRequired,
};

UserReviewDialog.defaultProps = {
  setPurchaseDate: false,
  pendingRequest: false,
  myReview: null,
  requestError: null,
};

UserReviewDialog.contextType = AppContext;

export default connect(
  state => ({
    isOpen: state.areas.review.open,
    requestError: state.areas.review.requestError,
    pendingRequest: state.areas.review.pendingRequest,
  }),
  actions
)(UserReviewDialog);
